import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, RotateCcw, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { labelsAPI } from '../../lib/api';

const SNAP = 500; // grille 0,5 mm (µm)
const snap = v => Math.round(v / SNAP) * SNAP;
const KIND_LABEL = { text: 'Texte', paragraph: 'Bloc de texte', barcode: 'Code-barres EAN13', image: 'Logo' };

function NumField({ id, value, step = 0.1, min, onCommit, testid }) {
    const [draft, setDraft] = useState(null);
    useEffect(() => { setDraft(null); }, [id]);
    return (
        <Input type="number" step={step} value={draft ?? value} className="h-7 text-xs" data-testid={testid}
            onChange={e => {
                setDraft(e.target.value);
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) onCommit(min != null ? Math.max(min, v) : v);
            }}
            onBlur={() => setDraft(null)} />
    );
}

export default function LabelAdjusterDialog({ clientId, template, products = [], scope = 'template', product = null, onClose }) {
    const tplProducts = products.filter(p => p.label?.template_id === template.id);
    const [sampleId, setSampleId] = useState(product?.id || tplProducts[0]?.id || products[0]?.id || null);
    const [layout, setLayout] = useState(null);
    const [adj, setAdj] = useState({});
    const [selected, setSelected] = useState([]);
    const [scale, setScale] = useState(1.4);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const readyRef = useRef(false);
    const timerRef = useRef(null);
    const dragRef = useRef(null);
    const changedRef = useRef(false);
    const [, setTick] = useState(0);
    const today = new Date().toISOString().slice(0, 10);

    const load = useCallback(async (adjustments) => {
        if (!sampleId) return;
        setBusy(true);
        try {
            const r = await labelsAPI.layout({
                client_id: clientId, product_id: sampleId, template_id: template.id, date: today,
                adjustments: scope === 'template' ? adjustments : null,
                product_adjustments: scope === 'product' ? adjustments : null,
            });
            setLayout(r.data);
            if (!readyRef.current) {
                setAdj(scope === 'template' ? (r.data.saved_adjustments || {}) : (r.data.saved_product_adjustments || {}));
                readyRef.current = true;
            }
        } catch { toast.error("Impossible de charger l'étiquette"); }
        setBusy(false);
    }, [clientId, sampleId, template.id, scope, today]);

    useEffect(() => { load(readyRef.current ? adj : null); }, [sampleId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!readyRef.current) return;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => load(adj), 350);
        return () => clearTimeout(timerRef.current);
    }, [adj]); // eslint-disable-line react-hooks/exhaustive-deps

    const patch = (name, changes) => setAdj(a => ({ ...a, [name]: { ...(a[name] || {}), ...changes } }));
    const patchMany = (entries) => setAdj(a => {
        const n = { ...a };
        for (const [name, changes] of entries) n[name] = { ...(n[name] || {}), ...changes };
        return n;
    });
    const nudge = (dxUm, dyUm) => {
        const entries = selected.map(name => {
            const o = layout.objects.find(x => x.name === name);
            return [name, { x: snap(o.x_um + dxUm), y: snap(o.y_um + dyUm) }];
        });
        patchMany(entries);
    };

    const startDrag = (e, o, mode) => {
        e.preventDefault(); e.stopPropagation();
        let names;
        if (e.ctrlKey || e.metaKey || e.shiftKey) {
            names = selected.includes(o.name) ? selected.filter(n => n !== o.name) : [...selected, o.name];
            setSelected(names);
            return; // ctrl/maj + clic = ajout/retrait de la sélection, sans glisser
        }
        names = selected.includes(o.name) ? selected : [o.name];
        setSelected(names);
        if (mode !== 'move') names = [o.name];
        const sx = e.clientX, sy = e.clientY;
        const factor = layout.um_to_px * scale;
        const onMove = ev => { dragRef.current = { names, mode, dxPx: ev.clientX - sx, dyPx: ev.clientY - sy }; setTick(t => t + 1); };
        const onUp = ev => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            const dx = (ev.clientX - sx) / factor, dy = (ev.clientY - sy) / factor;
            dragRef.current = null; setTick(t => t + 1);
            if (Math.abs(dx) < 100 && Math.abs(dy) < 100) return;
            if (mode === 'move') {
                patchMany(names.map(name => {
                    const obj = layout.objects.find(x => x.name === name);
                    return [name, { x: snap(obj.x_um + dx), y: snap(obj.y_um + dy) }];
                }));
            } else if (o.kind === 'paragraph') {
                patch(o.name, { w: Math.max(3000, snap(o.w_um + dx)) });
            } else if (o.kind === 'image') {
                patch(o.name, { w: Math.max(2000, snap(o.w_um + dx)), h: Math.max(2000, snap(o.h_um + dy)) });
            } else if (o.kind === 'barcode') {
                patch(o.name, {
                    module_w_um: Math.max(200, Math.round(o.module_w_um + dx / 95)),
                    bar_h_um: Math.max(3000, Math.round(o.bar_h_um + dy)),
                });
            }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const save = async () => {
        setSaving(true);
        try {
            if (scope === 'template') await labelsAPI.saveTplAdjust(template.id, adj);
            else await labelsAPI.saveProdAdjust(clientId, product.id, adj);
            toast.success('Ajustements enregistrés — aperçus et impressions mis à jour');
            onClose(true);
        } catch { toast.error('Enregistrement impossible'); setSaving(false); }
    };

    const reset = async () => {
        const msg = scope === 'template'
            ? 'Rétablir la maquette d\'origine (.nlbl) pour ce modèle ?'
            : 'Supprimer l\'ajustement spécifique de ce produit et revenir au réglage du modèle ?';
        if (!window.confirm(msg)) return;
        try {
            if (scope === 'template') await labelsAPI.resetTplAdjust(template.id);
            else await labelsAPI.resetProdAdjust(clientId, product.id);
            changedRef.current = true;
            setAdj({});
            toast.success(scope === 'template' ? 'Maquette d\'origine rétablie' : 'Retour au réglage du modèle');
        } catch { toast.error('Erreur'); }
    };

    const sel = selected.length === 1 ? (layout?.objects?.find(o => o.name === selected[0]) || null) : null;
    const av = (field, fallback) => {
        const a = sel && adj[sel.name] ? adj[sel.name][field] : undefined;
        return a !== undefined && a !== null ? a : fallback;
    };
    const factor = layout ? layout.um_to_px * scale : 1;
    const mm = v => (v == null ? '' : (v / 1000).toFixed(1));
    const tickPx = 5000 * (layout?.um_to_px || 0.008) * scale;
    const rulerStyle = dir => ({
        background: `repeating-linear-gradient(${dir}, #cbd5e1 0, #cbd5e1 1px, transparent 1px, transparent ${tickPx}px)`,
    });

    return (
        <Dialog open onOpenChange={() => onClose(changedRef.current)}>
            <DialogContent className="max-w-5xl sm:max-w-5xl max-h-[92vh] flex flex-col" data-testid="label-adjuster-dialog">
                <DialogHeader>
                    <DialogTitle className="text-base">
                        Ajuster — {template.name}{scope === 'product' && product ? ` · ${product.name} (spécifique)` : ''}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-xs text-slate-500">Produit d'exemple</Label>
                    <Select value={sampleId || ''} onValueChange={setSampleId} disabled={scope === 'product'}>
                        <SelectTrigger className="h-7 text-xs w-56" data-testid="adjuster-sample-select"><SelectValue placeholder="Produit…" /></SelectTrigger>
                        <SelectContent>{(tplProducts.length ? tplProducts : products).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex items-center gap-1 ml-auto">
                        <button onClick={() => setSelected(selected.length === (layout?.objects?.length || 0) ? [] : (layout?.objects || []).map(o => o.name))}
                            className="px-2 py-0.5 rounded text-[11px] border bg-white text-slate-600 hover:border-teal-500"
                            data-testid="adjuster-select-all">
                            {selected.length === (layout?.objects?.length || 0) && selected.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
                        </button>
                        {[1, 1.4, 2].map(z => (
                            <button key={z} onClick={() => setScale(z)}
                                className={`px-2 py-0.5 rounded text-[11px] border ${scale === z ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500'}`}
                                data-testid={`adjuster-zoom-${z}`}>{z}×</button>
                        ))}
                        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-1" />}
                    </div>
                </div>
                <div className="flex gap-3 flex-1 min-h-0">
                    <div className="flex-1 overflow-auto bg-slate-100 rounded border" data-testid="adjuster-canvas">
                        {layout ? (
                            <div className="relative mx-auto my-5" style={{ width: layout.width * scale + 14, paddingLeft: 14, paddingTop: 14 }}>
                                <div className="absolute left-14 top-0 right-0 h-3 opacity-50" style={rulerStyle('to right')} />
                                <div className="absolute top-14 left-0 bottom-0 w-3 opacity-50" style={rulerStyle('to bottom')} />
                                <div className="relative bg-white shadow-md select-none" style={{ width: layout.width * scale, height: layout.height * scale }}
                                    onPointerDown={() => setSelected([])}>
                                    <img src={`data:image/png;base64,${layout.image}`} alt="" draggable={false}
                                        className="absolute inset-0 w-full h-full pointer-events-none" />
                                    {layout.objects.map(o => {
                                        const isSel = selected.includes(o.name);
                                        const drag = dragRef.current?.names?.includes(o.name) ? dragRef.current : null;
                                        const style = {
                                            left: o.box.x * scale, top: o.box.y * scale,
                                            width: Math.max(8, o.box.w * scale), height: Math.max(8, o.box.h * scale),
                                            transform: drag && drag.mode === 'move' ? `translate(${drag.dxPx}px, ${drag.dyPx}px)` : undefined,
                                        };
                                        return (
                                            <div key={o.name} className={`absolute cursor-move ${isSel ? 'border-2 border-teal-500 bg-teal-500/5' : 'border border-dashed border-slate-300/70 hover:border-teal-400'}`}
                                                style={style} onPointerDown={e => startDrag(e, o, 'move')} title={o.name}
                                                data-testid={`adjuster-box-${o.name}`}>
                                                {isSel && selected.length === 1 && o.kind !== 'text' && (
                                                    <div className="absolute -right-1.5 -bottom-1.5 w-3 h-3 bg-teal-600 rounded-sm cursor-nwse-resize"
                                                        onPointerDown={e => startDrag(e, o, 'resize')} data-testid={`adjuster-resize-${o.name}`} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
                    </div>
                    <div className="w-60 shrink-0 space-y-2 overflow-y-auto" data-testid="adjuster-panel">
                        {!sel && selected.length < 2 && <p className="text-xs text-slate-400 pt-4">Cliquez un élément de l'étiquette pour le sélectionner, puis glissez-le ou réglez-le ici. Ctrl+clic (ou Maj+clic) pour sélectionner plusieurs éléments. Grille d'aimantation : 0,5 mm.</p>}
                        {selected.length > 1 && (
                            <div className="space-y-2 pt-2" data-testid="adjuster-group-panel">
                                <p className="text-xs font-semibold">{selected.length} éléments sélectionnés</p>
                                <p className="text-[10px] text-slate-400 -mt-1">Glissez l'un d'eux pour déplacer tout le groupe, ou utilisez les flèches (pas de 0,5 mm).</p>
                                <div className="grid grid-cols-3 gap-1 w-28">
                                    <span />
                                    <Button variant="outline" size="sm" className="h-7 w-8 p-0" onClick={() => nudge(0, -500)} data-testid="adjuster-group-up">▲</Button>
                                    <span />
                                    <Button variant="outline" size="sm" className="h-7 w-8 p-0" onClick={() => nudge(-500, 0)} data-testid="adjuster-group-left">◀</Button>
                                    <span />
                                    <Button variant="outline" size="sm" className="h-7 w-8 p-0" onClick={() => nudge(500, 0)} data-testid="adjuster-group-right">▶</Button>
                                    <span />
                                    <Button variant="outline" size="sm" className="h-7 w-8 p-0" onClick={() => nudge(0, 500)} data-testid="adjuster-group-down">▼</Button>
                                    <span />
                                </div>
                                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500 px-1" onClick={() => setSelected([])} data-testid="adjuster-group-clear">
                                    Désélectionner
                                </Button>
                            </div>
                        )}
                        {sel && (
                            <>
                                <p className="text-xs font-semibold">{sel.name}</p>
                                <p className="text-[10px] text-slate-400 -mt-1">{KIND_LABEL[sel.kind] || sel.kind}</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-[10px]">X (mm)</Label>
                                        <NumField id={`${sel.name}-x`} value={mm(av('x', sel.x_um))} testid="adjuster-x-input"
                                            onCommit={v => patch(sel.name, { x: Math.round(v * 1000) })} />
                                    </div>
                                    <div>
                                        <Label className="text-[10px]">Y (mm)</Label>
                                        <NumField id={`${sel.name}-y`} value={mm(av('y', sel.y_um))} testid="adjuster-y-input"
                                            onCommit={v => patch(sel.name, { y: Math.round(v * 1000) })} />
                                    </div>
                                </div>
                                {sel.kind !== 'image' && (
                                    <>
                                        <div>
                                            <Label className="text-[10px]">Taille de police (pt){sel.kind === 'barcode' ? ' — chiffres' : ''}</Label>
                                            <div className="flex items-center gap-1">
                                                <Button variant="outline" size="sm" className="h-7 w-7 p-0" data-testid="adjuster-font-minus"
                                                    onClick={() => patch(sel.name, { font_size: Math.max(4, av('font_size', sel.font_size || 8) - 0.5) })}><Minus className="w-3 h-3" /></Button>
                                                <NumField id={`${sel.name}-font`} value={av('font_size', sel.font_size) ?? ''} step={0.5} min={4} testid="adjuster-font-input"
                                                    onCommit={v => patch(sel.name, { font_size: v })} />
                                                <Button variant="outline" size="sm" className="h-7 w-7 p-0" data-testid="adjuster-font-plus"
                                                    onClick={() => patch(sel.name, { font_size: av('font_size', sel.font_size || 8) + 0.5 })}><Plus className="w-3 h-3" /></Button>
                                            </div>
                                        </div>
                                        {sel.kind !== 'barcode' && (
                                            <div className="flex items-center gap-2">
                                                <Switch checked={!!av('bold', sel.bold)} onCheckedChange={v => patch(sel.name, { bold: v })} data-testid="adjuster-bold-switch" />
                                                <Label className="text-[10px]">Gras</Label>
                                            </div>
                                        )}
                                    </>
                                )}
                                {sel.kind === 'paragraph' && (
                                    <div>
                                        <Label className="text-[10px]">Largeur du bloc (mm)</Label>
                                        <NumField id={`${sel.name}-w`} value={mm(av('w', sel.w_um))} step={0.5} min={3} testid="adjuster-width-input"
                                            onCommit={v => patch(sel.name, { w: Math.round(v * 1000) })} />
                                    </div>
                                )}
                                {sel.kind === 'image' && (
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-[10px]">Largeur (mm)</Label>
                                            <NumField id={`${sel.name}-w`} value={mm(av('w', sel.w_um))} step={0.5} min={2} testid="adjuster-width-input"
                                                onCommit={v => patch(sel.name, { w: Math.round(v * 1000) })} />
                                        </div>
                                        <div>
                                            <Label className="text-[10px]">Hauteur (mm)</Label>
                                            <NumField id={`${sel.name}-h`} value={mm(av('h', sel.h_um))} step={0.5} min={2} testid="adjuster-height-input"
                                                onCommit={v => patch(sel.name, { h: Math.round(v * 1000) })} />
                                        </div>
                                    </div>
                                )}
                                {sel.kind === 'barcode' && (
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-[10px]">Hauteur barres (mm)</Label>
                                            <NumField id={`${sel.name}-barh`} value={mm(av('bar_h_um', sel.bar_h_um))} step={0.5} min={3} testid="adjuster-barh-input"
                                                onCommit={v => patch(sel.name, { bar_h_um: Math.round(v * 1000) })} />
                                        </div>
                                        <div>
                                            <Label className="text-[10px]">Module (µm)</Label>
                                            <NumField id={`${sel.name}-module`} value={Math.round(av('module_w_um', sel.module_w_um) || 0)} step={10} min={200} testid="adjuster-module-input"
                                                onCommit={v => patch(sel.name, { module_w_um: Math.round(v) })} />
                                        </div>
                                        <p className="col-span-2 text-[10px] text-slate-400">Minimum garanti scannable : module 200 µm, barres 3 mm.</p>
                                    </div>
                                )}
                                {adj[sel.name] && (
                                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500 px-1" data-testid="adjuster-reset-object"
                                        onClick={() => setAdj(a => { const n = { ...a }; delete n[sel.name]; return n; })}>
                                        <RotateCcw className="w-3 h-3 mr-1" />Réinitialiser cet élément
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DialogFooter className="flex-row justify-between sm:justify-between">
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={reset} data-testid="adjuster-reset-all">
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />{scope === 'template' ? "Rétablir la maquette d'origine" : 'Revenir au réglage du modèle'}
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onClose(changedRef.current)} data-testid="adjuster-cancel">Annuler</Button>
                        <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="adjuster-save">
                            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Enregistrer
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
